"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { blogKeys } from "@/features/blog/shared/keys";
import type { BlogForClient } from "@/features/blog/shared/types";
import { usePathname, useRouter } from "@/i18n/navigation";
import { NotFound } from "@/lib/client/components/NotFound";
import { fetcher } from "@/lib/http/fetcher";
import type { IPaginatedResult, ISortCriteria } from "@/lib/shared/types/pagination";
import { Container, Text, VStack } from "@/ui";
import { BlogFeatured } from "./BlogFeatured";
import { BlogPagination } from "./BlogPagination";
import { BlogSection } from "./BlogSection";

interface BlogContainerProps {
  data: IPaginatedResult<BlogForClient>;
  title: string;
  subtitle: string;
}

/**
 * BlogContainer component - Main blog listing container
 * Separates featured post from masonry grid
 */
export const BlogContainer = ({ data, title, subtitle }: BlogContainerProps) => {
  const [page, setPage] = useState(data.page ?? 1);
  const [sortCriteria] = useState<ISortCriteria>({
    criteria: "priority",
    order: "asc",
  });
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("blog");

  const { data: blogs } = useQuery({
    queryKey: blogKeys.list(locale, page, sortCriteria),
    queryFn: async () =>
      await fetcher<IPaginatedResult<BlogForClient>>(
        `/${locale}/api/blog?locale=${locale}&page=${page}`,
      ),
    placeholderData: keepPreviousData,
    initialData: page === (data.page ?? 1) ? data : undefined,
    staleTime: 5 * 60 * 1000,
  });

  const onPageChange = (pageNum: number) => {
    setPage(pageNum);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set("page", String(pageNum));
    router.replace(`${pathname}?${currentParams}`);
  };

  if (!blogs) {
    return <NotFound />;
  }

  // Separate featured post (first) from remaining posts
  const featuredPost = blogs.docs?.[0];
  const remainingPosts = blogs.docs?.slice(1) || [];

  return (
    <Container className="py-16 md:py-20 lg:py-24">
      {/* Header */}
      <VStack className="mb-12 items-center text-center md:mb-16">
        <h1 className="font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
          {title}
        </h1>
        <Text className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          {subtitle}
        </Text>
      </VStack>

      {/* Featured Post */}
      {featuredPost && (
        <section className="mb-12 md:mb-16">
          <BlogFeatured post={featuredPost} />
        </section>
      )}

      {/* Masonry Grid */}
      {remainingPosts.length > 0 && (
        <section aria-label={t("all_categories")}>
          <BlogSection blogPosts={remainingPosts} />
        </section>
      )}

      {/* Pagination */}
      {blogs.hasNextPage || blogs.hasPrevPage ? (
        <BlogPagination
          pagination={blogs}
          onPageChange={onPageChange}
        />
      ) : null}
    </Container>
  );
};
